import { useLocale } from '@/app/providers/LocalContextProvider';
import { useMemo } from 'react';

// Translation types
type TranslationKey = string;
type TranslationParams = Record<string, string | number>;
type NestedTranslations = {
    [key: string]: string | NestedTranslations;
};

// Import all translation files
const translations: Record<string, NestedTranslations> = {
    en: require('@/locales/en.json'),
    fr: require('@/locales/fr.json'),
    de: require('@/locales/de.json'),
    ja: require('@/locales/ja.json'),
    tc: require('@/locales/tc.json'),
};

// Helper function to get nested translation value
function getNestedTranslation(obj: NestedTranslations, path: string): string {
    const result = path.split('.').reduce((current: any, key) => {
        if (current && typeof current === 'object' && key in current) {
            return current[key];
        }
        return undefined;
    }, obj);

    return typeof result === 'string' ? result : path;
}

export function useTranslation() {
    const { locale } = useLocale();

    const t = useMemo(() => {
        return (key: TranslationKey, paramsOrFallback?: TranslationParams | string, fallback?: string): string => {
            const currentTranslations = translations[locale] || translations.en;
            const translation = getNestedTranslation(currentTranslations, key);

            // Return translation if found, otherwise fallback or key
            let result = (translation && translation !== key) ? translation : (fallback || key);

            // Handle parameters
            let params: TranslationParams | undefined;
            if (typeof paramsOrFallback === 'object' && paramsOrFallback !== null) {
                params = paramsOrFallback;
            } else if (typeof paramsOrFallback === 'string') {
                // If second parameter is a string, it's the fallback
                result = (translation && translation !== key) ? translation : (paramsOrFallback || key);
            }

            // Interpolate parameters if provided
            if (params) {
                Object.keys(params).forEach(param => {
                    const placeholder = `{${param}}`;
                    result = result.replace(new RegExp(placeholder, 'g'), String(params[param]));
                });
            }

            return result;
        };
    }, [locale]);

    return { t, locale };
}

// Export available locales
export const availableLocales = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'tc', name: '繁體中文', flag: '🇹🇼' },
];